import * as Sentry from '@sentry/astro'
import ShopifyClient from 'shopify-buy'
import Cookies from 'js-cookie'
import axios from 'axios'
import buildShopifyPlugin from '@/services/shopify/lib/runtime/nuxt-shopify'
import { getNuxtShopifyModuleConfigs } from '@/services/shopify/lib/runtime/config.js'

const { next: config } = getNuxtShopifyModuleConfigs()

axios.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response.data
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error)
  },
)

/**
 * Takes an array of store configs and returns an object keyed by store handle
 * @param {array} stores - array of store configs
 * @returns {object} - object with each config keyed with store handle (EU, UK, US etc.)
 */
const buildStoreConfigs = (stores) => {
  return stores.reduce((storeConfigs, store) => {
    storeConfigs[store.handle] = store
    return storeConfigs
  }, {})
}

/**
 * Builds the shopify GraphQL client with optional override to force single requests to use a different store
 * @param {object} initialStoreConfig - store which matches current users locale
 * @param {object} moduleOptions - options passed to module as well as some defaults
 * @param {object} integrations - an object of integrations keyed by their name
 * @param {array} stores - array of store configs from the Nuxt config
 * @returns {function} - Shopify GraphQL client
 */
const buildShopifyGraphQLClient = (
  initialStoreConfig,
  { storefrontVersion },
  { axios },
  stores,
) => {
  const availbleStoreConfigs = buildStoreConfigs(stores, { storefrontVersion })

  return (query, storeOverride = null) => {
    try {
      const overrideStoreConfig = storeOverride
        ? getOverrideStoreConfig(availbleStoreConfigs, storeOverride)
        : null
      const { domain, token } = overrideStoreConfig || initialStoreConfig

      const url = `https://${domain}/api/${storefrontVersion}/graphql.json`

      return axios.post(url, query, {
        headers: {
          'X-Shopify-Storefront-Access-Token': `${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
    } catch (error) {
      console.log(error)
      Sentry.captureException(error)
    }
  }
}

/**
 * Checks the overriding store exists and has the key fields we need
 * @param {object} stores - object with each config keyed with store handle (EU, UK, US etc.)
 * @param {string} storeOverride - code of store to use as override (EU, UK, US etc.)
 * @returns {(object|null)} - store object which matches storeOverride or null if not found
 */
const getOverrideStoreConfig = (stores, storeOverride) => {
  const foundStore = stores[storeOverride] || null

  return foundStore.domain && foundStore.token ? foundStore : null
}

const buildShopifyClient = (storeConfig) => {
  return ShopifyClient.buildClient({
    domain: storeConfig.domain,
    storefrontAccessToken: storeConfig.token,
  })
}

let plugin = null

export default (localeCode, vuexStore) => {
  if (plugin) return plugin

  const { options, stores } = config

  if (!stores || stores.length === 0) {
    throw new Error('[nuxt-shopify] stores property is required')
  }

  let integrations = {}

  integrations.axios = axios
  integrations.vuex = vuexStore

  if (!Cookies) throw new Error('[nuxt-shopify] missing Cookies module')
  integrations.cookies = Cookies

  const defaultOptions = {
    strategy: 'default',
    vuexStore: null,
    storefrontVersion: '2024-01',
  }

  const moduleOptions = {
    ...defaultOptions,
    ...options,
  }

  let initialStore = config.stores[0]

  if (!localeCode) return

  const foundStore = config.stores.find((store) => {
    const storeHasLocale = store.locales.findIndex(
      (locale) => locale === localeCode,
    )
    return storeHasLocale !== -1
  })

  initialStore = foundStore || initialStore

  const client = buildShopifyClient(initialStore, options)
  const graphQLClient = buildShopifyGraphQLClient(
    initialStore,
    moduleOptions,
    integrations,
    stores,
  )

  const nuxtShopifyPlugin = buildShopifyPlugin(
    client,
    graphQLClient,
    moduleOptions,
    integrations,
  )

  plugin = nuxtShopifyPlugin
  return plugin
}
